/**
 * resolvePlacementBox -- pick the correct placement-zone box for the
 * Stage.vue overlay given the current placement + view selection.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase F F3.c -- catalog-driven placement-zone overlay.
 *
 * The Stage overlay shows where decorated artwork will sit on the
 * garment. Two inputs decide which box to render:
 *
 *   1. The selected placement's zone slug (e.g. left_chest, full_back).
 *      Resolves to the SKU's live Artifi view ``Code`` (e.g. "Left Chest",
 *      "Center Back") via ``strictViewCodeForSku`` (data-driven off
 *      ``skuViews``; #2224 removed the static zone->view map).
 *
 *   2. The currently active camera view -- ``selectedView`` -- also an
 *      Artifi view Code (e.g. "Full Center" for the front of a
 *      sweatshirt, "Center Back" for the back).
 *
 * Each catalog box lives on one view (the one whose camera angle frames
 * that zone). It is ALSO part of a view group ("Front" / "Rear"). The
 * overlay should render only when the zone's group matches the
 * selectedView's group -- e.g. a FULL_BACK zone never renders on a
 * Front view, even when the placement is selected.
 *
 * Returns either:
 *   * a box ``{ view_code, group_name, image_width_px, image_height_px,
 *               x_px, y_px, w_px, h_px, source }`` -- render the overlay
 *   * ``null`` -- hide the overlay (no catalog row, wrong view group,
 *     or unmapped placement)
 *
 * Hiding on miss is intentional: a fabricated overlay positioned
 * wrong (the bug this file fixes) is worse than no overlay.
 */
import {
    strictViewCodeForSku,
    frontGroupCodesForSku,
    backGroupCodesForSku,
} from '@/utils/placementZoneToViewCode'

/**
 * @param {object} args
 * @param {string|null} args.zoneCode      e.g. "LEFT_CHEST" or "left_chest"
 * @param {string|null} args.selectedView  e.g. "Left Chest" or "Center Back"
 * @param {Array<object>} args.boxes       From usePlacementZoneBoxes().boxes.value
 * @param {Array<{code, group?}>|null} [args.skuViews]  live Artifi views --
 *   when present, authoritative: a zone whose view isn't a real view for
 *   this product resolves to null (overlay hidden). When absent, falls open
 *   to the legacy static map.
 * @returns {object|null}                  Box to render, or null to hide
 */
export function resolvePlacementBox({ zoneCode, selectedView, boxes, skuViews = null }) {
    if (!Array.isArray(boxes) || boxes.length === 0) return null
    if (!zoneCode) return null

    // SKU-aware: strictViewCodeForSku returns the real view code, or null
    // when the zone has no valid view for this product (with live views) --
    // in which case we hide the overlay rather than draw a fabricated box.
    // Falls open to the static map when skuViews are absent.
    const wantedViewCode = strictViewCodeForSku(zoneCode, skuViews)
    if (!wantedViewCode) return null

    const candidate = boxes.find(
        (b) => b && b.view_code === wantedViewCode,
    )
    if (!candidate) return null

    // View-gating: only render the overlay if the zone's view group matches
    // the active camera view. Without this, a rear-group zone box draws on a
    // front view. Group membership is SKU-aware (resilient to Artifi's
    // group=None), falling back to the static group sets when views absent.
    const frontSet = frontGroupCodesForSku(skuViews)
    const backSet = backGroupCodesForSku(skuViews)
    const gn = candidate.group_name
    const boxGroupSet =
        gn === 'Front' ? frontSet
            : gn === 'Rear' || gn === 'Back' ? backSet
                : null
    if (boxGroupSet && selectedView && !boxGroupSet.has(selectedView)) {
        return null
    }

    return candidate
}
